/* cannot call this constants.js because it collides with constants module in NodeJS */

import { flatten, indexBy } from 'util/data'
import { riskColors, interventionColors } from './colors'
import RAW_INTERVENTION_CATEGORIES from './interventionCategories.json'
import RAW_STATES from './states.json'
import nodeCommandInfo from './commands.json'

export { nodeCommandInfo }

export {
  urlParamCodes,
  toURLParamCode,
  areaParams,
  defaultCompareArea,
  validateAreaParams,
  getCompareAreaLabel,
  getCompareAreaOptions,
  getTransitionCompareArea,
  getAreaType,
  getStateId,
  reportingUnitsLabel,
} from './areas'
export { colormaps, colormapIndex, colormapGradient } from './colors'
export { siteMetadata } from '../../gatsby-config'

// only one model for now; hardcode it for use in search widget
export const MODEL_ID = 'heat_1991_2020_3days'

export const STATES = indexBy(RAW_STATES, 'id')

export const interventionCategories = RAW_INTERVENTION_CATEGORIES.map(
  ({ id: categoryId, interventions, ...category }, i) => ({
    id: categoryId,
    ...category,
    url: `/interventions/${categoryId}/`,
    order: i + 1,
    interventions: interventions.map(
      ({ id: interventionId, ...intervention }) => ({
        id: interventionId,
        category: categoryId,
        ...intervention,
        url: `/interventions/${categoryId}/${interventionId}/`,
      })
    ),
  })
)

export const interventionCategoryIndex = Object.fromEntries(
  interventionCategories.map((category) => [category.id, category])
)

export const allInterventions = flatten(
  interventionCategories.map(({ interventions }) => interventions)
)

export const interventionIndex = Object.fromEntries(
  allInterventions.map((intervention) => [intervention.id, intervention])
)

export const riskLevels = [
  { id: 'low', label: 'Low', color: riskColors[0] },
  { id: 'modlow', label: 'Moderate-low', color: riskColors[1] },
  { id: 'mod', label: 'Moderate', color: riskColors[2] },
  { id: 'modhigh', label: 'Moderate-high', color: riskColors[3] },
  { id: 'high', label: 'High', color: riskColors[4] },
]
export const riskLevelIndex = indexBy(riskLevels, 'id')

export const riskFactors = [
  { id: 'hazard', label: 'Hazard', units: 'temperature' },
  { id: 'exposure', label: 'Exposure', units: 'population density' },
  { id: 'vulnerability', label: 'Vulnerability', units: 'multiple factors' },
]
export const riskFactorIndex = indexBy(riskFactors, 'id')

export const interventionLevels = [
  { id: 'not', label: 'Not recommended', color: interventionColors[0] },
  { id: 'less', label: 'Less recommended', color: interventionColors[1] },
  { id: 'mod', label: 'Moderately recommended', color: interventionColors[2] },
  { id: 'more', label: 'More recommended', color: interventionColors[3] },
  { id: 'high', label: 'Highly recommended', color: interventionColors[4] },
]
export const interventionLevelIndex = indexBy(interventionLevels, 'id')

export const interventionFactors = [
  {
    id: 'goodness_of_fit',
    label: 'Goodness of Fit',
    description:
      'Intervention effectiveness and utility, or fit, is affected by environmental, social, and demographic factors. Specific factors were identified through a comprehensive literature review. Some interventions are broadly applicable and effective in a wide range of settings, while others are only likely to be effective under certain conditions. Goodness of fit for these interventions was modeled using combinations of factors that strongly impact the intervention’s effectiveness.',
    // TODO: better level labels
    levels: [
      { id: 'poor', label: 'Bad fit', color: interventionColors[0] },
      { id: 'less', label: 'Poor fit', color: interventionColors[1] },
      { id: 'mod', label: 'Moderate fit', color: interventionColors[2] },
      { id: 'more', label: 'Good fit', color: interventionColors[3] },
      { id: 'high', label: 'Best fit', color: interventionColors[4] },
    ],
    rangeLabel: 'better fit',
  },
  {
    id: 'effectiveness',
    label: 'Effectiveness',
    description:
      'Intervention effectiveness is assessed using the RE-AIM framework, which describes the intervention’s reach, or penetration into and across populations; its effectiveness, or strength of its protective effect; its adoption, or uptake in settings where it has been introduced; its implementation, or the fidelity and consistency with which it is implemented; and its maintenance, or persistence once adopted. For each intervention, the literature was reviewed, each domain was scored according to a standard rubric, and the scores were combined to model effectiveness.',
    levels: [
      { id: 'not', label: 'Not effective', color: interventionColors[0] },
      { id: 'less', label: 'Less effective', color: interventionColors[1] },
      {
        id: 'mod',
        label: 'Moderately effective',
        color: interventionColors[2],
      },
      { id: 'more', label: 'More effective', color: interventionColors[3] },
      { id: 'high', label: 'Highly effective', color: interventionColors[4] },
    ],
    rangeLabel: 'more effective',
  },
  {
    id: 'ease',
    label: 'Ease of Implementation',
    description:
      'Ease of implementation is affected by the ease of recruiting participants and administering and coordinating intervention activities, the degree of upheaval and disruption associated with intervention implementation, and regulatory challenges that may affect implementation. For each intervention, these considerations were reviewed in the literature, assessed using a standardized rubric, and combined to model implementation ease.',
    levels: [
      { id: 'hard', label: 'Hard', color: interventionColors[0] },
      { id: 'less', label: 'Less easy', color: interventionColors[1] },
      {
        id: 'mod',
        label: 'Moderately easy',
        color: interventionColors[2],
      },
      { id: 'more', label: 'More easy', color: interventionColors[3] },
      { id: 'easy', label: 'Easy', color: interventionColors[4] },
    ],
    rangeLabel: 'easier',
  },
  {
    id: 'amenity',
    label: 'Amenity Value',
    description:
      'In addition to their public health effectiveness, interventions may have amenity values or concerns that could factor into implementation decisions. An intervention’s impact on community aesthetics, opportunities for leisure and community, and property values were reviewed and assessed using a standardized rubric and combined to model amenity value.',
    levels: [
      { id: 'low', label: 'Low value', color: interventionColors[0] },
      {
        id: 'modlow',
        label: 'Moderately-low value',
        color: interventionColors[1],
      },
      {
        id: 'mod',
        label: 'Moderate value',
        color: interventionColors[2],
      },
      {
        id: 'modhigh',
        label: 'Moderately-high value',
        color: interventionColors[3],
      },
      { id: 'high', label: 'High value', color: interventionColors[4] },
    ],
    rangeLabel: 'higher value',
  },
  // NOTE: cost scales inverse: highest value = lowest cost (most affordable)
  {
    id: 'cost',
    label: 'Cost',
    description:
      'Intervention costs include capital and operational outlays. While these cannot always be measured with precision, they can be estimated through review of the literature. Capital and operational costs, standardized by community size and for a five-year time horizon, were estimated for a local health department and its partners in implementing specific interventions.',
    levels: [
      { id: 'low', label: 'Unaffordable', color: interventionColors[0] },
      { id: 'less', label: 'Less affordable', color: interventionColors[1] },
      {
        id: 'mod',
        label: 'Moderately affordable',
        color: interventionColors[2],
      },
      { id: 'more', label: 'More affordable', color: interventionColors[3] },
      { id: 'high', label: 'Highly affordable', color: interventionColors[4] },
    ],
    rangeLabel: 'more affordable',
  },
]
export const interventionFactorIndex = indexBy(interventionFactors, 'id')

export const missingDataLevel = {
  id: 'missing',
  label: 'Insufficient data',
  color: '#AAAAAA',
}

export const defaultExtent = [
  -124.9022044, 17.83087146, -65.16797072, 52.88065373,
]
